/**
 * 司机档案车队配置
 */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Input, Button, List, Modal, message, Spin, Pagination } from 'antd';
import { ExclamationCircleOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import { companyListRequest } from '@/service/companyManage';
import {
  getDriverRiskProfileCompanyConfig,
  addDriverRiskProfileCompanyConfig,
  deleteDriverRiskProfileCompanyConfig,
} from '@/service/driverArchive';
import { DriverRiskProfileCompanyConf } from '@/service/driverArchive/define';
import styles from './index.module.scss';

interface CompanyItem {
  id: number;
  name: string;
}

export default function DriverArchiveCompanyConfig() {
  // 左侧所有车队
  const [allFleets, setAllFleets] = useState<CompanyItem[]>([]);
  const [allFleetsLoading, setAllFleetsLoading] = useState(false);
  const [allFleetsSearchName, setAllFleetsSearchName] = useState('');
  const [allFleetsPage, setAllFleetsPage] = useState(1);
  const [allFleetsPageSize] = useState(10);
  const [allFleetsTotalCount, setAllFleetsTotalCount] = useState(0);

  // 右侧已配置车队
  const [configuredFleets, setConfiguredFleets] = useState<DriverRiskProfileCompanyConf[]>([]);
  const [configuredFleetsLoading, setConfiguredFleetsLoading] = useState(false);
  const [configuredFleetsSearchName, setConfiguredFleetsSearchName] = useState('');
  const [configuredFleetsPage, setConfiguredFleetsPage] = useState(1);
  const [configuredFleetsPageSize] = useState(10);
  const [configuredFleetsTotalCount, setConfiguredFleetsTotalCount] = useState(0);

  // 加载所有车队列表
  const fetchAllFleets = async () => {
    setAllFleetsLoading(true);
    try {
      const response = await companyListRequest({
        page: allFleetsPage,
        limit: allFleetsPageSize,
        name: allFleetsSearchName,
      });

      if (response.data?.success) {
        setAllFleets(response.data?.data || []);
        setAllFleetsTotalCount(response.data?.count || 0);
      } else {
        message.error(response.data?.msg || '获取车队列表失败');
      }
    } catch (error) {
      console.error('获取车队列表失败:', error);
      message.error('获取车队列表失败');
    } finally {
      setAllFleetsLoading(false);
    }
  };

  // 加载已配置车队列表
  const fetchConfiguredFleets = async () => {
    setConfiguredFleetsLoading(true);
    try {
      const response = await getDriverRiskProfileCompanyConfig({
        page: configuredFleetsPage,
        limit: configuredFleetsPageSize,
        companyName: configuredFleetsSearchName,
      });

      if (response.data?.success) {
        setConfiguredFleets(response.data?.data || []);
        setConfiguredFleetsTotalCount(response.data?.count || 0);
      } else {
        message.error(response.data?.msg || '获取已配置车队列表失败');
      }
    } catch (error) {
      console.error('获取已配置车队列表失败:', error);
      message.error('获取已配置车队列表失败');
    } finally {
      setConfiguredFleetsLoading(false);
    }
  };

  // 初始加载
  useEffect(() => {
    fetchAllFleets();
  }, [allFleetsPage, allFleetsSearchName]);

  useEffect(() => {
    fetchConfiguredFleets();
  }, [configuredFleetsPage, configuredFleetsSearchName]);

  // 添加车队配置
  const handleAddConfig = (fleet: CompanyItem) => {
    Modal.confirm({
      title: '确认添加',
      icon: <ExclamationCircleOutlined />,
      content: `确定要将车队"${fleet.name}"添加到拍照配置吗?`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await addDriverRiskProfileCompanyConfig({
            companyId: fleet.id,
            companyName: fleet.name,
          });

          if (response.data?.success) {
            message.success('添加成功');
            // 刷新两侧列表
            fetchAllFleets();
            fetchConfiguredFleets();
          } else {
            message.error(response.data?.msg || '添加失败');
          }
        } catch (error) {
          console.error('添加配置失败:', error);
          message.error('添加失败，请重试');
        }
      },
    });
  };

  // 删除车队配置
  const handleDeleteConfig = (fleet: DriverRiskProfileCompanyConf) => {
    Modal.confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: `确定要将车队"${fleet.companyName}"从拍照配置中移除吗?`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await deleteDriverRiskProfileCompanyConfig(fleet.id!);

          if (response.data?.success) {
            message.success('删除成功');
            // 刷新两侧列表
            fetchAllFleets();
            fetchConfiguredFleets();
          } else {
            message.error(response.data?.msg || '删除失败');
          }
        } catch (error) {
          console.error('删除配置失败:', error);
          message.error('删除失败，请重试');
        }
      },
    });
  };

  // 左侧搜索
  const handleAllFleetsSearch = () => {
    setAllFleetsPage(1);
    fetchAllFleets();
  };

  // 右侧搜索
  const handleConfiguredFleetsSearch = () => {
    setConfiguredFleetsPage(1);
    fetchConfiguredFleets();
  };

  return (
    <Card className={styles.pageContent}>
      <Row gutter={24} className={styles.transferContainer}>
        {/* 左侧：所有车队 */}
        <Col span={11}>
          <Card
            title="所有车队"
            className={styles.fleetListCard}
            extra={
              <div className={styles.searchBox}>
                <Input
                  placeholder="搜索车队名称"
                  value={allFleetsSearchName}
                  onChange={(e) => setAllFleetsSearchName(e.target.value)}
                  onPressEnter={handleAllFleetsSearch}
                  style={{ width: 200, marginRight: 8 }}
                  allowClear
                />
                <Button type="primary" onClick={handleAllFleetsSearch}>
                  搜索
                </Button>
              </div>
            }
          >
            <Spin spinning={allFleetsLoading}>
              <List
                className={styles.fleetList}
                dataSource={allFleets}
                renderItem={(item) => (
                  <List.Item
                    className={styles.fleetListItem}
                    actions={[
                      <Button
                        type="link"
                        icon={<RightOutlined />}
                        onClick={() => handleAddConfig(item)}
                      >
                        添加
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={item.name}
                      description={`车队ID: ${item.id}`}
                    />
                  </List.Item>
                )}
              />
              <div className={styles.paginationWrapper}>
                <Pagination
                  current={allFleetsPage}
                  pageSize={allFleetsPageSize}
                  total={allFleetsTotalCount}
                  onChange={(page) => setAllFleetsPage(page)}
                  showSizeChanger={false}
                  showTotal={(total) => `共 ${total} 条`}
                />
              </div>
            </Spin>
          </Card>
        </Col>

        {/* 中间：箭头 */}
        <Col span={2} className={styles.arrowContainer}>
          <div className={styles.arrowBox}>
            <RightOutlined className={styles.arrowIcon} />
            <LeftOutlined className={styles.arrowIcon} />
          </div>
        </Col>

        {/* 右侧：已配置拍照功能的车队 */}
        <Col span={11}>
          <Card
            title="已配置拍照功能的车队"
            className={styles.fleetListCard}
            extra={
              <div className={styles.searchBox}>
                <Input
                  placeholder="搜索车队名称"
                  value={configuredFleetsSearchName}
                  onChange={(e) => setConfiguredFleetsSearchName(e.target.value)}
                  onPressEnter={handleConfiguredFleetsSearch}
                  style={{ width: 200, marginRight: 8 }}
                  allowClear
                />
                <Button type="primary" onClick={handleConfiguredFleetsSearch}>
                  搜索
                </Button>
              </div>
            }
          >
            <Spin spinning={configuredFleetsLoading}>
              <List
                className={styles.fleetList}
                dataSource={configuredFleets}
                renderItem={(item) => (
                  <List.Item
                    className={styles.fleetListItem}
                    actions={[
                      <Button
                        type="link"
                        danger
                        icon={<LeftOutlined />}
                        onClick={() => handleDeleteConfig(item)}
                      >
                        移除
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={item.companyName}
                      description={`车队ID: ${item.companyId} | 配置时间: ${item.createTime || '-'}`}
                    />
                  </List.Item>
                )}
              />
              <div className={styles.paginationWrapper}>
                <Pagination
                  current={configuredFleetsPage}
                  pageSize={configuredFleetsPageSize}
                  total={configuredFleetsTotalCount}
                  onChange={(page) => setConfiguredFleetsPage(page)}
                  showSizeChanger={false}
                  showTotal={(total) => `共 ${total} 条`}
                />
              </div>
            </Spin>
          </Card>
        </Col>
      </Row>
    </Card>
  );
}
